import type {
  Place,
  Review,
  Route,
  SavedList,
  User,
  AuthTokens,
  CreatePlaceRequest,
  CreateReviewRequest,
  BBox,
} from '@wingman/shared';

const API_URL = import.meta.env.VITE_API_URL || '/api';

function getToken(): string | null {
  return localStorage.getItem('access_token');
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refresh_token');
}

function setTokens(tokens: AuthTokens) {
  localStorage.setItem('access_token', tokens.accessToken);
  localStorage.setItem('refresh_token', tokens.refreshToken);
}

function clearTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401 && token) {
    const refreshed = await refreshToken();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${getToken()}`;
      const retryResponse = await fetch(`${API_URL}${path}`, { ...options, headers });
      if (!retryResponse.ok) {
        throw new Error((await retryResponse.json()).error || 'Request failed');
      }
      return retryResponse.json();
    }
    clearTokens();
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

async function refreshToken(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) return false;

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });
    if (!response.ok) return false;
    const { data } = await response.json();
    setTokens(data.tokens);
    return true;
  } catch {
    return false;
  }
}

export const api = {
  setTokens,
  clearTokens,
  getToken,

  auth: {
    register: async (email: string, password: string, name: string) => {
      const result = await request<{ data: { user: User; tokens: AuthTokens } }>(
        '/auth/register',
        { method: 'POST', body: JSON.stringify({ email, password, name }) },
      );
      setTokens(result.data.tokens);
      return result.data.user;
    },

    login: async (email: string, password: string) => {
      const result = await request<{ data: { user: User; tokens: AuthTokens } }>(
        '/auth/login',
        { method: 'POST', body: JSON.stringify({ email, password }) },
      );
      setTokens(result.data.tokens);
      return result.data.user;
    },

    logout: () => {
      clearTokens();
    },

    oauthLogin: (provider: 'google' | 'osm') => {
      window.location.href = `${API_URL}/auth/oauth/${provider}`;
    },

    handleOAuthCallback: (): boolean => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const tokensParam = params.get('tokens');
      if (tokensParam) {
        try {
          const tokens = JSON.parse(decodeURIComponent(tokensParam)) as AuthTokens;
          setTokens(tokens);
          window.history.replaceState({}, document.title, window.location.pathname);
          return true;
        } catch {
          return false;
        }
      }
      return false;
    },

    forgotPassword: (email: string) =>
      request<{ data: { message: string } }>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    resetPassword: (token: string, password: string) =>
      request<{ data: { message: string } }>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      }),
  },

  places: {
    getByBBox: (bbox: BBox, options: { category?: string; openNow?: boolean; minRating?: number; sortBy?: 'distance' | 'rating' | 'name'; signal?: AbortSignal } = {}) => {
      const params = new URLSearchParams({
        bbox: `${bbox.minLng},${bbox.minLat},${bbox.maxLng},${bbox.maxLat}`,
        centerLat: String((bbox.minLat + bbox.maxLat) / 2),
        centerLng: String((bbox.minLng + bbox.maxLng) / 2),
        sortBy: options.sortBy || 'distance',
      });
      if (options.category) params.set('category', options.category);
      if (options.openNow) params.set('openNow', 'true');
      if (options.minRating && options.minRating > 0) params.set('minRating', String(options.minRating));
      return request<{ data: Place[]; total: number; limit: number; offset: number }>(`/places?${params}`, { signal: options.signal });
    },

    getById: (id: string) => request<{ data: Place }>(`/places/${id}`),

    create: (data: CreatePlaceRequest) =>
      request<{ data: Place }>('/places', { method: 'POST', body: JSON.stringify(data) }),

    reportIssue: (id: string, data: { kind: 'closed' | 'moved' | 'duplicate' | 'wrong_details' | 'unsafe'; note?: string }) =>
      request<{ data: { id: string; kind: string; status: string; created_at: string } }>(`/places/${id}/reports`, { method: 'POST', body: JSON.stringify(data) }),

    getReviews: (id: string) =>
      request<{ data: Review[] }>(`/places/${id}/reviews`),

    addReview: (id: string, data: CreateReviewRequest) =>
      request<{ data: Review }>(`/places/${id}/reviews`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    uploadReviewPhoto: (placeId: string, reviewId: string, file: File) => {
      const formData = new FormData();
      formData.append('photo', file);
      return request<{ data: { id: string; url: string } }>(
        `/places/${placeId}/reviews/${reviewId}/photos`,
        { method: 'POST', body: formData },
      );
    },
  },

  search: {
    query: (q: string, lat?: number, lng?: number, category?: string) => {
      const params = new URLSearchParams({ q });
      if (lat !== undefined) params.set('lat', String(lat));
      if (lng !== undefined) params.set('lng', String(lng));
      if (category) params.set('category', category);
      return request<{ data: Place[] }>(`/search?${params}`);
    },

    autocomplete: (q: string) =>
      request<{ data: Array<{ id: string; name: string; lat: number; lng: number }> }>(
        `/search/autocomplete?q=${encodeURIComponent(q)}`,
      ),

    geocode: (q: string) => request<{ data: unknown }>(`/search/geocode?q=${encodeURIComponent(q)}`),

    reverse: (lat: number, lng: number) =>
      request<{ data: unknown }>(`/search/reverse?lat=${lat}&lng=${lng}`),
  },

  routing: {
    getRoute: (
      fromLat: number,
      fromLng: number,
      toLat: number,
      toLng: number,
      mode: 'driving' | 'walking' | 'cycling',
    ) => {
      const params = new URLSearchParams({
        fromLat: String(fromLat),
        fromLng: String(fromLng),
        toLat: String(toLat),
        toLng: String(toLng),
        mode,
      });
      return request<{ data: Route }>(`/route?${params}`);
    },
  },

  user: {
    getSaved: () => request<{ data: Place[] }>('/user/saved'),
    savePlace: (placeId: string, listId?: string) =>
      request<{ data: { saved: boolean } }>('/user/saved', {
        method: 'POST',
        body: JSON.stringify({ placeId, listId }),
      }),
    unsavePlace: (placeId: string) =>
      request<{ data: { saved: boolean } }>(`/user/saved/${placeId}`, { method: 'DELETE' }),
    getLists: () => request<{ data: SavedList[] }>('/user/lists'),
    createList: (name: string, description?: string) =>
      request<{ data: SavedList }>('/user/lists', {
        method: 'POST',
        body: JSON.stringify({ name, description }),
      }),
    updateList: (id: string, data: { name?: string; description?: string; isPublic?: boolean }) =>
      request<{ data: SavedList }>(`/user/lists/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    deleteList: (id: string) =>
      request<{ data: { deleted: boolean } }>(`/user/lists/${id}`, { method: 'DELETE' }),
    addPlaceToList: (listId: string, placeId: string) =>
      request<{ data: { added: boolean } }>(`/user/lists/${listId}/places`, {
        method: 'POST',
        body: JSON.stringify({ placeId }),
      }),
    removePlaceFromList: (listId: string, placeId: string) =>
      request<{ data: { removed: boolean } }>(`/user/lists/${listId}/places/${placeId}`, { method: 'DELETE' }),
    getReviews: () => request<{ data: Review[] }>('/user/reviews'),
    getProfile: () => request<{ data: User & { bio?: string } }>('/user/profile'),
    updateProfile: (data: { name?: string; bio?: string }) =>
      request<{ data: User }>('/user/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    uploadAvatar: (file: File) => {
      const formData = new FormData();
      formData.append('avatar', file);
      return request<{ data: { avatarUrl: string } }>('/user/avatar', {
        method: 'POST',
        body: formData,
      });
    },
    follow: (userId: string) =>
      request<{ data: { following: boolean } }>(`/user/follow/${userId}`, { method: 'POST' }),
    unfollow: (userId: string) =>
      request<{ data: { following: boolean } }>(`/user/follow/${userId}`, { method: 'DELETE' }),
    getFollowing: () => request<{ data: Array<{ id: string; name: string; avatarUrl?: string }> }>('/user/following'),
    getFollowers: () => request<{ data: Array<{ id: string; name: string; avatarUrl?: string }> }>('/user/followers'),
    getFeed: () => request<{ data: Array<{ id: string; type: string; userId: string; userName: string; userAvatarUrl?: string; placeName?: string; createdAt: string }> }>('/user/feed'),
    getPublicProfile: (userId: string) =>
      request<{ data: User & { bio?: string; reviewCount: number; listCount: number; followerCount: number; followingCount: number } }>(`/user/${userId}`),
  },

  lists: {
    getPublic: (id: string) =>
      request<{ data: { list: SavedList & { ownerName?: string }; places: Place[] } }>(`/lists/${id}`),
  },

  osm: {
    startNycImport: () =>
      request<{ data: { id: string; status: string; current_batch: number; total_batches: number; imported: number; updated: number; skipped: number; error_summary?: string } }>('/osm/import-jobs/nyc-core', { method: 'POST' }),
    getImportJobs: () =>
      request<{ data: Array<{ id: string; status: string; current_batch: number; total_batches: number; imported: number; updated: number; skipped: number; error_summary?: string; requested_at: string }> }>('/osm/import-jobs'),
    sync: () =>
      request<{ data: { synced: number; failed: number; changesetId?: number; pending?: number; message?: string } }>('/osm/sync', { method: 'POST' }),
    syncIncremental: (bbox: { south: number; west: number; north: number; east: number }) => {
      const params = new URLSearchParams({
        south: String(bbox.south),
        west: String(bbox.west),
        north: String(bbox.north),
        east: String(bbox.east),
      });
      return request<{ data: { imported: number; updated: number; skipped: number; total: number; since: string } }>(`/osm/sync/incremental?${params}`, { method: 'POST' });
    },
    getEdits: (status?: string) =>
      request<{ data: Array<{ id: string; place_id: string; field: string; old_value: unknown; new_value: unknown; status: string; place_name: string; osm_id: number; created_at: string }> }>(`/osm/edits${status ? `?status=${status}` : ''}`),
    getSyncLog: () =>
      request<{ data: Array<{ id: string; place_id: string; osm_changeset_id: number; action: string; status: string; error_message?: string; place_name?: string; created_at: string }> }>('/osm/sync/log'),
  },
};
